import { useI18n } from 'vue-i18n'

export const DATE_FORMATS = {
  date: {},
  dayMonth: { day: 'numeric', month: 'short' },
  weekdayDayMonth: { weekday: 'short', day: 'numeric', month: 'short' },
  time: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
} satisfies Record<string, Intl.DateTimeFormatOptions>

export const NUMBER_FORMATS = {
  decimal: { style: 'decimal', maximumFractionDigits: 2 }
} satisfies Record<string, Intl.NumberFormatOptions>

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'] as const

export type DateFormatName = keyof typeof DATE_FORMATS

export type NumberFormatName = keyof typeof NUMBER_FORMATS

export type DateFormat = DateFormatName | Intl.DateTimeFormatOptions

export type NumberFormat = NumberFormatName | Intl.NumberFormatOptions

export type DateInput = Date | string | number | null | undefined

const dates = new Map<string, Intl.DateTimeFormat>()

const numbers = new Map<string, Intl.NumberFormat>()

const dateFormatter = (locale: string, format: DateFormat) => {
  const named = typeof format === 'string'
  const key = `${locale}|${named ? format : JSON.stringify(format)}`
  const formatter = dates.get(key) ?? new Intl.DateTimeFormat(locale, named ? DATE_FORMATS[format] : format)
  dates.set(key, formatter)
  return formatter
}

const numberFormatter = (locale: string, format: NumberFormat) => {
  const named = typeof format === 'string'
  const key = `${locale}|${named ? format : JSON.stringify(format)}`
  const formatter = numbers.get(key) ?? new Intl.NumberFormat(locale, named ? NUMBER_FORMATS[format] : format)
  numbers.set(key, formatter)
  return formatter
}

const toDate = (value: DateInput) => {
  if (value === null || value === undefined || value === '') return
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export const useFormat = () => {
  const { locale } = useI18n()

  const date = (value: DateInput, format: DateFormat = 'date') => {
    const parsed = toDate(value)
    return parsed ? dateFormatter(locale.value, format).format(parsed) : ''
  }

  const dateRange = (start: DateInput, end: DateInput, format: DateFormat = 'date') => {
    const from = toDate(start)
    const to = toDate(end)
    return from && to ? dateFormatter(locale.value, format).formatRange(from, to) : ''
  }

  const number = (value: number | null | undefined, format: NumberFormat = 'decimal') =>
    value === null || value === undefined || !Number.isFinite(value) ? '' : numberFormatter(locale.value, format).format(value)

  const currency = (value: number | null | undefined, code: string, format?: Intl.NumberFormatOptions) =>
    number(value, { ...format, style: 'currency', currency: code })

  const size = (bytes: number | null | undefined, format: NumberFormat = 'decimal') => {
    if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return ''
    const exponent = bytes === 0 ? 0 : Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1)
    return `${number(bytes / 1024 ** exponent, format)} ${BYTE_UNITS[exponent]}`
  }

  return { date, dateRange, number, currency, size }
}
